import { AutocompleteInput } from "@/components/admin/autocomplete-input";
import { ReferenceInput } from "@/components/admin/reference-input";
import { SelectInput } from "@/components/admin/select-input";
import { TextInput } from "@/components/admin/text-input";
import { required, useRecordContext } from "ra-core";
import { BooleanInput, DateTimeInput } from "@/components/admin";
import { Badge } from "@/components/ui/badge";

import { useConfigurationContext } from "../root/ConfigurationContext";
import { defaultTaskCategories } from "../root/defaultConfiguration";
import type { Sale } from "../types";

// Assignee option: teammate name + their role tag. The role is what the new
// task's category will derive from, so it's surfaced right in the picker.
const SaleOptionRender = () => {
  const record = useRecordContext<Sale>();
  const { taskCategories } = useConfigurationContext();
  if (!record) return null;
  const roleLabel = (taskCategories ?? defaultTaskCategories).find(
    (c) => c.value === record.role,
  )?.label;
  return (
    <div className="flex items-center gap-2">
      <span>
        {record.first_name} {record.last_name}
      </span>
      {roleLabel && (
        <Badge variant="secondary" className="text-xs">
          {roleLabel}
        </Badge>
      )}
    </div>
  );
};
const saleOptionText = <SaleOptionRender />;

export const TaskFormContent = ({
  showSales,
  showDone,
  showContact,
  showCategory = true,
  showType = true,
}: {
  showSales?: boolean;
  showDone?: boolean;
  /**
   * The contact a task is about: optional and clearable on every surface that
   * shows it, create and edit alike. Never required — the v1.7.0 complaint was
   * being made to pick one. A task created from a Contact page arrives with it
   * filled in and still editable. LCATOMICZO-31.
   */
  showContact?: boolean;
  /**
   * Create flows hide the category input — it derives from the assignee's role
   * on submit. Edit flows keep it (manual category edits always win).
   */
  showCategory?: boolean;
  /** Create flows hide the type input too — new tasks always get type "none". */
  showType?: boolean;
}) => {
  const { taskTypes, taskCategories } = useConfigurationContext();
  return (
    <div className="flex flex-col gap-4">
      <TextInput
        autoFocus
        source="text"
        validate={required()}
        multiline
        className="m-0"
        helperText={false}
      />
      {showSales && (
        <ReferenceInput source="sales_id" reference="sales">
          <AutocompleteInput
            label="Assignee"
            optionText={saleOptionText}
            helperText={false}
          />
        </ReferenceInput>
      )}

      {showContact && (
        <ReferenceInput source="contact_id" reference="contacts" perPage={10}>
          {/* No `validate`: that is what shows the clear control. Clearing (and
              re-picking the current choice) writes "", which PostgREST rejects
              for a bigint column, so parse it back to null. */}
          <AutocompleteInput
            label="resources.tasks.fields.contact_id"
            parse={(value) => value || null}
            helperText={false}
          />
        </ReferenceInput>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* due_date + type are nullable in the schema — pre-filled with
            defaults but not required, matching the Zoom task form. */}
        <DateTimeInput source="due_date" helperText={false} />
        {showType && (
          <SelectInput
            source="type"
            choices={taskTypes}
            optionText="label"
            optionValue="value"
            defaultValue="none"
            helperText={false}
          />
        )}
        {/* category is NOT NULL in the schema, so it stays required. */}
        {showCategory && (
          <SelectInput
            source="category"
            validate={required()}
            choices={taskCategories ?? defaultTaskCategories}
            optionText="label"
            optionValue="value"
            defaultValue="sales"
            helperText={false}
          />
        )}
      </div>
      {showDone && (
        <BooleanInput
          source="done_date"
          label="Done"
          format={(v) => !!v}
          parse={(v) => (v ? new Date().toISOString() : null)}
          helperText={false}
        />
      )}
    </div>
  );
};
